import { Link } from "react-router-dom";
import { Instagram, Facebook, Twitter, Mail, Phone, MessageCircle, MapPin } from "lucide-react";
import Logo from "./Logo";
import { BUSINESS, SOCIALS, waLink } from "../data/packages";

const explore = [
  { to: "/domestic-packages", label: "Domestic Packages" },
  { to: "/international-packages", label: "International Packages" },
  { to: "/group-trips", label: "Group Trips" },
  { to: "/customised-trips", label: "Customised Trips" },
];

const company = [
  { to: "/", label: "Home" },
  { to: "/about-us", label: "About Us" },
  { to: "/contact-us", label: "Contact Us" },
];

const policies = [
  { to: "/policies", label: "Booking Terms" },
  { to: "/policies", label: "Cancellation Policy" },
  { to: "/policies", label: "Privacy Policy" },
  { to: "/policies", label: "Refund Policy" },
];

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="relative overflow-hidden border-t border-white/5 bg-obsidian/60">
      <div className="pointer-events-none absolute -top-40 left-1/2 h-80 w-[42rem] -translate-x-1/2 rounded-full bg-nebula/15 blur-[140px]" />
      <div className="relative mx-auto max-w-7xl px-6 pb-10 pt-16 md:pt-20">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Logo />
            <p className="mt-6 max-w-xs text-sm leading-relaxed text-slate-400">
              Curated domestic and international journeys, small-group departures and bespoke routes —
              designed so you arrive a traveller and leave belonging.
            </p>
            <div className="mt-6 flex gap-3">
              {[
                { href: SOCIALS.instagram, Icon: Instagram, label: "Instagram" },
                { href: SOCIALS.facebook, Icon: Facebook, label: "Facebook" },
                { href: SOCIALS.twitter, Icon: Twitter, label: "Twitter" },
              ]
                .filter((s) => s.href)
                .map(({ href, Icon, label }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`footer-social-${label.toLowerCase()}`}
                    aria-label={label}
                    className="rounded-full border border-white/10 p-2.5 text-slate-400 transition-all duration-300 hover:border-aurora/50 hover:text-aurora"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
            </div>
          </div>

          <div className="lg:col-span-2">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-aurora">Explore</p>
            <ul className="mt-5 space-y-3">
              {explore.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} data-testid={`footer-link-${l.to.slice(1)}`} className="text-sm text-slate-400 transition-colors hover:text-slate-100">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-aurora">Company</p>
            <ul className="mt-5 space-y-3">
              {company.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-sm text-slate-400 transition-colors hover:text-slate-100">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-aurora">Policies</p>
            <ul className="mt-5 space-y-3">
              {policies.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-sm text-slate-400 transition-colors hover:text-slate-100">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-aurora">Reach Us</p>
            <ul className="mt-5 space-y-3 text-sm text-slate-400">
              <li className="flex items-start gap-2.5">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-comet" />
                <span>
                  {BUSINESS.phonePrimary}
                  <br />
                  {BUSINESS.phoneSecondary}
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <MessageCircle className="mt-0.5 h-4 w-4 shrink-0 text-comet" />
                <a
                  href={waLink(`Hi ${BUSINESS.name}!`)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-whatsapp-link"
                  className="transition-colors hover:text-aurora"
                >
                  WhatsApp us anytime
                </a>
              </li>
              {BUSINESS.email && (
                <li className="flex items-start gap-2.5">
                  <Mail className="mt-0.5 h-4 w-4 shrink-0 text-comet" />
                  <a href={`mailto:${BUSINESS.email}`} data-testid="footer-email-link" className="transition-colors hover:text-aurora">
                    {BUSINESS.email}
                  </a>
                </li>
              )}
              <li className="flex items-start gap-2.5">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-comet" />
                <span>{BUSINESS.cities}</span>
              </li>
            </ul>
          </div>
        </div>

        <p aria-hidden="true" className="pointer-events-none mt-16 select-none text-center font-display text-[11.5vw] font-extrabold leading-none tracking-[-0.03em] text-white/[0.04]">
          COSMOS ROUTES
        </p>

        <div className="mt-4 flex flex-col items-center justify-between gap-3 border-t border-white/5 pt-8 sm:flex-row">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
            © 2026 Cosmos Routes · All rights reserved
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
            Explore · Experience · Belong
          </p>
        </div>
      </div>
    </footer>
  );
}
